import { getResponse, http, HttpResponse, type RequestHandler } from 'msw';

import { DemoProfile } from '../types';
// Relative rather than bare like the other demo handlers: payroll.test.ts runs under the unit-test
// config, and demo/ is outside that config's tsconfig paths.
import overviewHandlers from '../../src/modules/Payroll/Overview/demo/demoHandlers';
import teamHandlers from '../../src/modules/Payroll/Team/demo/demoHandlers';
import documentHandlers from '../../src/modules/Payroll/Documents/demo/demoHandlers';
import contractorPaymentHandlers from '../../src/modules/Payroll/ContractorPayments/demo/demoHandlers';
import runPayrollHandlers from '../../src/modules/Payroll/RunPayroll/demo/demoHandlers';
import {
    buildPendingRun,
    MOCK_ACTIVITY,
    MOCK_OVERVIEW_CORE,
} from '../../src/services/payroll/queries/usePayrollOverview/usePayrollOverview.mocks';
import type {
    PayrollActivityPage,
    PayrollActivityRun,
    PayrollOverviewCore,
} from '../../src/services/payroll/queries/usePayrollOverview/usePayrollOverview.interface';
import type {
    AutoPayrollState,
    CompensationReport,
    PayrollCommandAccepted,
    PayScheduleList,
    PayScheduleSummary,
    WorkerBankAccountList,
} from '../../src/services/payroll/api/generated/v1/model';
import { SETUP_STEPS, setupStateFixture, setupSteps } from '../../src/modules/Payroll/common/setupStateFixtures';

import buildDocumentHandlers from './payrollDocuments';

const PAYROLL_API = '*/api/payroll/v1/*';

const PRIMARY_SCHEDULE_UUID = 'schedule-demo-1';
const SEMIMONTHLY_SCHEDULE_UUID = 'schedule-demo-2';
const AUTO_PAYROLL_COMMAND_UUID = 'c0mm4nd0-0002-4c3a-9b7e-1f2a3b4c5d62';

const SCHEDULES = {
    monthly: {
        pay_schedule_uuid: PRIMARY_SCHEDULE_UUID,
        custom_name: 'Monthly payroll',
        frequency: 'monthly',
        day_1: 31,
        day_2: null,
        anchor_pay_date: '2026-01-31',
        period_end_anchor: '2026-01-31',
        is_primary: true,
    },
    semimonthly: {
        pay_schedule_uuid: SEMIMONTHLY_SCHEDULE_UUID,
        custom_name: 'Semi-monthly payroll',
        frequency: 'semimonthly',
        day_1: 15,
        day_2: 31,
        anchor_pay_date: '2026-05-15',
        period_end_anchor: null,
        is_primary: false,
    },
} as const;

type ScheduleKey = keyof typeof SCHEDULES;

/**
 * Who each schedule pays. The Team fixture leaves most rows unscheduled, which the Pay schedules
 * page reports as a warning; the monthly three are the people on the run the Overview opens.
 */
const SCHEDULE_BY_WORKER: Record<string, ScheduleKey> = {
    'employee-ada-lovelace': 'monthly',
    'employee-grace-hopper': 'monthly',
    'employee-jordan-kim': 'monthly',
    'employee-margaret-hamilton': 'semimonthly',
    'employee-noor-patel': 'semimonthly',
    'employee-dorothy-vaughan': 'semimonthly',
};

const TEAM_LIST_PATH = /\/api\/payroll\/v1\/team\/$/;
const EMPLOYEE_DETAIL_PATH = /\/api\/payroll\/v1\/team\/employees\/[^/]+\/$/;

const COMPENSATION_YEAR = 2026;
const OWNER_PAID_TO_DATE = 48333;
/** $98,000 a year on the monthly schedule. */
const OWNER_MONTHLY_PAY = 8166.67;

const LAST_MONTH_INDEX = 11;

/**
 * Pay dates still ahead in the compensation year, as of `asOf`, on the owner's monthly schedule,
 * which pays on the last day of each month. Zero outside that year, as the report's contract says.
 * UTC throughout, so the count does not move with the viewer's time zone.
 */
export const remainingOwnerPayDates = (asOf: Date): number => {
    if (asOf.getUTCFullYear() !== COMPENSATION_YEAR) {
        return 0;
    }
    const month = asOf.getUTCMonth();
    const payDay = new Date(Date.UTC(COMPENSATION_YEAR, month + 1, 0)).getUTCDate();
    const thisMonthStillAhead = asOf.getUTCDate() < payDay;
    return LAST_MONTH_INDEX - month + (thisMonthStillAhead ? 1 : 0);
};

const PROJECTED_MONTHS = 3;
/** Days to step back from each weekday (Sunday first) to reach a business day. */
const DAYS_BACK_TO_FRIDAY = [2, 0, 0, 0, 0, 0, 1];

/**
 * A schedule's check dates over the three months from `asOf`, earliest first, as the provider lists
 * them: a day past the month's end lands on its last day, and a weekend one moves back to the
 * Friday. UTC throughout, like the count above.
 */
export const projectedPayDates = (key: ScheduleKey, asOf: Date): string[] => {
    const { day_1: day1, day_2: day2 } = SCHEDULES[key];
    const days = day2 === null ? [day1] : [day1, day2];
    const today = Date.UTC(asOf.getUTCFullYear(), asOf.getUTCMonth(), asOf.getUTCDate());
    return Array.from({ length: PROJECTED_MONTHS }, (_, offset) => asOf.getUTCMonth() + offset)
        .flatMap((month) => {
            const lastDay = new Date(Date.UTC(asOf.getUTCFullYear(), month + 1, 0)).getUTCDate();
            return days.map((day) => {
                const date = new Date(Date.UTC(asOf.getUTCFullYear(), month, Math.min(day, lastDay)));
                date.setUTCDate(date.getUTCDate() - DAYS_BACK_TO_FRIDAY[date.getUTCDay()]);
                return date;
            });
        })
        .filter((date) => date.getTime() >= today)
        .map((date) => date.toISOString().slice(0, 10));
};

const LATE_RUN_COUNT = 2;

/**
 * The semi-monthly schedule's last two pay days, both missed, so the Overview has a Late list to
 * show. Its pay periods run the 1st to the 15th and the 16th to the month's end.
 */
const buildLateRuns = (asOf: Date): PayrollActivityRun[] => {
    const today = asOf.toISOString().slice(0, 10);
    const twoMonthsAgo = new Date(Date.UTC(asOf.getUTCFullYear(), asOf.getUTCMonth() - 2, 1));
    return projectedPayDates('semimonthly', twoMonthsAgo)
        .filter((checkDate) => checkDate < today)
        .slice(-LATE_RUN_COUNT)
        .map((checkDate, index) => {
            const [year, month, day] = checkDate.split('-').map(Number);
            const firstHalf = day <= SCHEDULES.semimonthly.day_1;
            const lastDay = new Date(Date.UTC(year, month, 0)).getUTCDate();
            const monthPrefix = checkDate.slice(0, 8);
            return buildPendingRun({
                payroll_uuid: `late-demo-${index + 1}`,
                pay_schedule_uuid: SEMIMONTHLY_SCHEDULE_UUID,
                period_start_date: `${monthPrefix}${firstHalf ? '01' : '16'}`,
                period_end_date: `${monthPrefix}${firstHalf ? '15' : lastDay}`,
                check_date: checkDate,
                frequency_label: 'Semi-monthly payroll',
                display_status: 'OVERDUE',
            });
        });
};

/** State the demo's own handlers share, so a toggle on one page is what the next page reads. */
interface DemoPayrollState {
    isAutoPayrollOn: () => boolean;
    setAutoPayrollOn: (enabled: boolean) => void;
}

const createState = (): DemoPayrollState => {
    let autoPayrollOn = MOCK_OVERVIEW_CORE.auto_payroll_enabled ?? false;
    return {
        isAutoPayrollOn: () => autoPayrollOn,
        setAutoPayrollOn: (enabled) => {
            autoPayrollOn = enabled;
        },
    };
};

const scheduleSummary = (key: ScheduleKey, state: DemoPayrollState): PayScheduleSummary => ({
    ...SCHEDULES[key],
    auto_payroll_enabled: key === 'monthly' && state.isAutoPayrollOn(),
});

type WorkerRow = { worker_uuid?: string; pay_schedule?: PayScheduleSummary | null };

const withSchedule = (row: WorkerRow, state: DemoPayrollState): WorkerRow => {
    const key = row.worker_uuid ? SCHEDULE_BY_WORKER[row.worker_uuid] : undefined;
    return key ? { ...row, pay_schedule: scheduleSummary(key, state) } : row;
};

const assignSchedules = (path: string, body: unknown, state: DemoPayrollState): unknown => {
    if (TEAM_LIST_PATH.test(path)) {
        const page = body as { results?: WorkerRow[] };
        return { ...page, results: page.results?.map((row) => withSchedule(row, state)) };
    }
    if (EMPLOYEE_DETAIL_PATH.test(path)) {
        return withSchedule(body as WorkerRow, state);
    }
    return body;
};

/**
 * The Storybook fixtures were written for engineers, so some of them borrow famous names or read
 * as placeholders. A prospect should see one believable company, so the demo renames on the way
 * out rather than editing fixtures that unit tests assert on. Capitalised names only: worker uuids
 * carry lowercase name slugs (`employee-ada-lovelace`) that detail routes are keyed on.
 */
const buildRenames = (profile: DemoProfile): [string, string][] => [
    // The owner is the demo persona on every page, whichever fixture drew the row.
    ['Ada Lovelace', `${profile.first_name} ${profile.last_name}`],
    ['Jon Kuminga', `${profile.first_name} ${profile.last_name}`],
    ['Ada', profile.first_name],
    ['Jon', profile.first_name],
    ['Lovelace', profile.last_name],
    ['Kuminga', profile.last_name],
    ['Grace Hopper', 'Daniel Okafor'],
    ['Moses Moody', 'Daniel Okafor'],
    ['Grace', 'Daniel'],
    ['Moses', 'Daniel'],
    ['Hopper', 'Okafor'],
    ['Moody', 'Okafor'],
    ['Margaret Hamilton', 'Rachel Goldberg'],
    ['Tobias Lindgren', 'Rachel Goldberg'],
    ['Margaret', 'Rachel'],
    ['Tobias', 'Rachel'],
    ['Hamilton', 'Goldberg'],
    ['Lindgren', 'Goldberg'],
    ['Brandin Podziemski', 'Jordan Kim'],
    ['Brandin', 'Jordan'],
    ['Podziemski', 'Kim'],
    ['Dorothy Vaughan', 'Elena Morales'],
    ['Dorothy', 'Elena'],
    ['Vaughan', 'Morales'],
    // Payroll documents draws its own people; map each onto a team member of the same kind.
    // Full names only, since several share a surname with someone already on the team.
    ['Alex Rivera', `${profile.first_name} ${profile.last_name}`],
    ['Jamie Employee', 'Rachel Goldberg'],
    ['Taylor Kim', 'Rachel Goldberg'],
    ['Jordan Blake', 'Noor Patel'],
    ['Sam Okafor', 'Daniel Okafor'],
    ['Casey Nolan', 'Elena Morales'],
    ['Riley Chen', 'Jordan Kim'],
    ['Jamie Contractor', 'Sofia Reyes'],
    ['Morgan Reyes', 'Priya Natarajan'],
    ['Drew Park', 'Tim Cowherd'],
];

const escapeRegExp = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const buildRenamer = (profile: DemoProfile) => {
    const renames = new Map(buildRenames(profile));
    const names = new RegExp(`\\b(${[...renames.keys()].map(escapeRegExp).join('|')})\\b`, 'g');
    const companyDomain = profile.email.split('@')[1];

    return (json: string) =>
        json
            .replace(names, (name) => renames.get(name) ?? name)
            // Work emails on the company's own domain rather than example.com, rebuilt from the
            // renamed display name so the two never disagree.
            .replace(/"([a-z]+)\.([a-z]+)@example\.com"/g, (_match, first: string, last: string) => {
                const capitalise = (part: string) => part.charAt(0).toUpperCase() + part.slice(1);
                const renamed = `${capitalise(first)} ${capitalise(last)}`.replace(names, (n) => renames.get(n) ?? n);
                return `"${renamed.toLowerCase().replace(' ', '.')}@${companyDomain}"`;
            });
};

/**
 * Endpoints the dashboard calls that have no Storybook handler. AutoPayroll is stateful so the
 * toggle on the Overview and on Pay schedules survives the refetch it triggers.
 */
const buildGapHandlers = (state: DemoPayrollState): RequestHandler[] => {
    const autoPayrollState = (): AutoPayrollState => ({
        as_of: new Date().toISOString(),
        nudge: null,
        primary_pay_schedule_uuid: PRIMARY_SCHEDULE_UUID,
        state: state.isAutoPayrollOn() ? 'on' : 'off',
        schedules: [
            {
                pay_schedule_uuid: PRIMARY_SCHEDULE_UUID,
                pay_schedule_name: 'Monthly payroll',
                is_primary: true,
                requested_enabled: state.isAutoPayrollOn(),
                effective_enabled: state.isAutoPayrollOn(),
                requested_at: null,
                auto_disabled_at: null,
                auto_disabled_reason: '',
            },
        ],
    });

    // The Overview fixture's next run predates the run-details fixture it opens; restate it to
    // match that run (three people, $15,861.06), or the card and the page it links to disagree.
    const overview = (): PayrollOverviewCore => ({
        ...MOCK_OVERVIEW_CORE,
        next_run: MOCK_OVERVIEW_CORE.next_run && {
            ...MOCK_OVERVIEW_CORE.next_run,
            total_to_debit: '15861.06',
            // Excludes the owner (`includes_owner` adds them), so this reads "You + 2 employees".
            employee_count: 2,
        },
        auto_payroll_enabled: state.isAutoPayrollOn(),
        active_pay_schedule_count: 2,
    });

    const commandAccepted = (commandType: string): PayrollCommandAccepted => ({
        command_type: commandType,
        command_uuid: AUTO_PAYROLL_COMMAND_UUID,
        poll_url: `/api/payroll/v1/commands/${AUTO_PAYROLL_COMMAND_UUID}/`,
        state: 'pending',
        synced: true,
    });

    // Skipping a late run submits it at $0, so it leaves the Late list and lands in history as Skipped.
    const lateRuns = buildLateRuns(new Date());
    const skippedRuns = new Set<string>();
    const activityRuns = (): PayrollActivityRun[] => [
        ...MOCK_ACTIVITY.results,
        ...lateRuns.map((run) =>
            skippedRuns.has(run.payroll_uuid)
                ? { ...run, display_status: 'SKIPPED' as const, total_amount: '0.00', can_skip: false }
                : run
        ),
    ];
    // Newest pay day first, as the server pages it. A schedule filter treats the fixture's
    // unscheduled rows as the monthly schedule's, which is what they are labelled.
    const activity = (request: Request): PayrollActivityPage => {
        const params = new URL(request.url).searchParams;
        const paySchedule = params.get('pay_schedule');
        const runs = activityRuns()
            .filter((run) => !paySchedule || (run.pay_schedule_uuid ?? PRIMARY_SCHEDULE_UUID) === paySchedule)
            .sort((a, b) => (b.check_date ?? '').localeCompare(a.check_date ?? ''));
        const limit = Number(params.get('limit') ?? runs.length);
        const offset = Number(params.get('offset') ?? 0);
        return { ...MOCK_ACTIVITY, count: runs.length, results: runs.slice(offset, offset + limit) };
    };

    // The monthly schedule owns the run the Overview opens; the other has nothing open.
    const openRun = (key: ScheduleKey) => (key === 'monthly' ? overview().next_run : null);
    // The fixture's run rolls with today rather than landing on a month end, so a schedule with one
    // open lists its pay dates from that run's deadline on, or the card would show a payday that
    // falls before the run it is waiting on.
    const payDatesFrom = (key: ScheduleKey): Date => {
        const deadline = openRun(key)?.run_payroll_deadline;
        return deadline ? new Date(deadline) : new Date();
    };

    const paySchedules = (): PayScheduleList => ({
        as_of: new Date().toISOString(),
        count: 2,
        results: (Object.keys(SCHEDULES) as ScheduleKey[]).map((key) => ({
            ...scheduleSummary(key, state),
            // The summary widens it to `string`; the list types it as the enum.
            frequency: SCHEDULES[key].frequency,
            name: '',
            auto_payroll_blockers: [],
            auto_payroll_blockers_known: true,
            employee_count: Object.values(SCHEDULE_BY_WORKER).filter((assigned) => assigned === key).length,
            next_run: openRun(key),
            projected_pay_dates: projectedPayDates(key, payDatesFrom(key)),
            projected_pay_dates_known: true,
        })),
    });

    const asOf = new Date();
    const remainingRuns = remainingOwnerPayDates(asOf);
    const compensation: CompensationReport = {
        year: COMPENSATION_YEAR,
        annual_salary: '98000.00',
        benchmark_low: '56000.00',
        benchmark_high: '120000.00',
        benchmark_suggested: '98000.00',
        paid_to_date: OWNER_PAID_TO_DATE.toFixed(2),
        expected_to_date: '56499.24',
        is_on_track: false,
        message: 'You have a $8,166.24 salary gap. Run a catch-up payroll to close it, or update your annual salary.',
        catch_up_amount: '8166.24',
        catch_up_unavailable_reason: null,
        projected_year_total: (OWNER_PAID_TO_DATE + remainingRuns * OWNER_MONTHLY_PAY).toFixed(2),
        // Paid to date plus the owner's regular pay on the pay dates left this year.
        scheduled_to_date: (OWNER_PAID_TO_DATE + remainingRuns * OWNER_MONTHLY_PAY).toFixed(2),
        remaining_runs: remainingRuns,
        verdict_unavailable_reason: null,
        projected_revenue: '170000.00',
        // estimated_distribution is projected_profit less annual_salary.
        projected_profit: '170000.00',
        estimated_distribution: '72000.00',
        // The demo team has no employee record for the owner, so there are no pay stubs to list.
        owner_employee_uuid: null,
        as_of: asOf.toISOString(),
    };

    const depositAccounts: WorkerBankAccountList = {
        count: 1,
        results: [
            {
                external_bank_account_id: 'bank-demo-1',
                name: 'Chase Total Checking',
                account_type: 'checking',
                hidden_account_number: 'XXXX3880',
            },
        ],
    };

    return [
        http.get('*/api/payroll/v1/overview/', () => HttpResponse.json(overview())),
        http.get('*/api/payroll/v1/auto-payroll/', () => HttpResponse.json(autoPayrollState())),
        http.post('*/api/payroll/v1/auto-payroll/enable/', () => {
            state.setAutoPayrollOn(true);
            return HttpResponse.json(commandAccepted('auto_payroll.enable'), { status: 202 });
        }),
        http.post('*/api/payroll/v1/auto-payroll/disable/', () => {
            state.setAutoPayrollOn(false);
            return HttpResponse.json(commandAccepted('auto_payroll.disable'), { status: 202 });
        }),
        http.get('*/api/payroll/v1/pay-schedules/', () => HttpResponse.json(paySchedules())),
        // The demo company finished setup long ago, so the Overview's setup card has nothing to say.
        http.get('*/api/payroll/v1/setup/', () =>
            HttpResponse.json(
                setupStateFixture({
                    onboarding_status: 'completed',
                    steps: setupSteps(SETUP_STEPS.length),
                    completed_at: '2026-01-15T17:00:00Z',
                })
            )
        ),
        http.get('*/api/payroll/v1/payrolls/', ({ request }) => HttpResponse.json(activity(request))),
        http.post('*/api/payroll/v1/payrolls/:payrollUuid/skip/', ({ params }) => {
            skippedRuns.add(params.payrollUuid as string);
            return HttpResponse.json(commandAccepted('payroll.skip'), { status: 202 });
        }),
        http.get('*/api/payroll/v1/compensation/', () => HttpResponse.json(compensation)),
        http.get('*/api/payroll/v1/compensation/deposit-account/', () => HttpResponse.json(depositAccounts)),
    ];
};

/**
 * The payroll dashboard's mocks, from the Storybook demo handlers each page already maintains.
 * First match wins: Team's stateful roster and command poll go ahead of Run payroll's copies, so a
 * worker added on My team is still there on the next page.
 */
export default function buildPayrollHandlers(profile: DemoProfile): RequestHandler[] {
    const state = createState();
    const documents = buildDocumentHandlers(profile);
    const payrollHandlers: RequestHandler[] = [
        ...documents.handlers,
        ...buildGapHandlers(state),
        ...Object.values(teamHandlers),
        ...Object.values(overviewHandlers),
        ...Object.values(documentHandlers),
        ...Object.values(contractorPaymentHandlers),
        ...Object.values(runPayrollHandlers),
    ];
    const rename = buildRenamer(profile);

    const renamingHandler = http.all(PAYROLL_API, async ({ request }) => {
        const response = await getResponse(payrollHandlers, request);
        if (!response?.headers.get('Content-Type')?.includes('application/json')) {
            return response;
        }
        const path = new URL(request.url).pathname;
        const body = JSON.parse(rename(JSON.stringify(assignSchedules(path, await response.json(), state))));
        documents.remember(body);
        return HttpResponse.json(documents.applySignatures(path, body), { status: response.status });
    });

    // Gusto's signer endpoints live off the payroll API, so they are matched directly.
    return [renamingHandler, ...payrollHandlers];
}
